import { useCallback, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { api, queryKeys } from '~/api'
import { SettingsLayout, SettingsSection } from '~/settings/components/settings-layout'
import { useAppSelector } from '~/store'
import UserDeleteConfirm from '../settings/components/Users/user-delete-confirm'
import UserDetailDialog from '../settings/components/Users/user-detail-dialog'
import UserList from '../settings/components/Users/user-list'
import UsersNotFound from '../settings/components/Users/users-not-found'

export function Users() {
    const accountOwnerId = useAppSelector((state) => state.initial.accountOwner?.id)
    const queryClient = useQueryClient()
    const queryKey = queryKeys.users.accountUserListing(accountOwnerId).queryKey

    const { data, isLoading, error } = api.users.accountUserListing.useQuery(queryKey, {
        params: { id: accountOwnerId },
    })

    const [searchTerm, setSearchTerm] = useState('')
    const [dialog, setDialog] = useState('')
    const [isCreatingUser, setIsCreatingUser] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState(-1)

    const filteredUsers = useMemo(() => {
        if (!data?.body) {
            return []
        } else if (!searchTerm) {
            return data.body
        }

        const text = searchTerm.toLowerCase()
        const regex = new RegExp(text, 'i')

        return data.body.filter((u) => {
            return (
                regex.test(u.first_name) ||
                regex.test(u.last_name) ||
                regex.test(u.email) ||
                regex.test(u.username)
            )
        })
    }, [data?.body, searchTerm])

    const selectedUser = useMemo(() => {
        return data?.body.find((u) => u.id === selectedUserId)
    }, [data?.body, selectedUserId])

    function onCloseUserDetailDialog(user: unknown) {
        if (user) {
            void queryClient.invalidateQueries({ queryKey })
        }

        setDialog('')
        setSelectedUserId(-1)
    }

    const renderUserList = useCallback(() => {
        if (isLoading) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={30} color="secondary" />
                </Box>
            )
        }

        if (error) {
            return (
                <Typography color="error">
                    {error.status === 403
                        ? gettext(error.body.error)
                        : gettext('Something went wrong')}
                </Typography>
            )
        }

        if (!filteredUsers.length) {
            return <UsersNotFound />
        }

        const handleEditUserList = (user: { id: number }) => {
            setIsCreatingUser(false)
            setSelectedUserId(user.id)
            setDialog('user-detail')
        }

        const handleDeleteUserList = (user: { id: number }) => {
            setSelectedUserId(user.id)
            setDialog('user-delete-confirm')
        }

        return (
            <UserList
                users={filteredUsers}
                onEdit={handleEditUserList}
                onDelete={handleDeleteUserList}
            />
        )
    }, [error, filteredUsers, isLoading])

    return (
        <SettingsLayout>
            <SettingsSection header={gettext('Users')}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: {
                            xs: 'column',
                            md: 'row',
                        },
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder={gettext('Search')}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="disabled" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <CancelIcon color="disabled" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <Button
                        sx={{ flexShrink: 0 }}
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setIsCreatingUser(true)
                            setDialog('user-detail')
                        }}
                    >
                        {gettext('Add user')}
                    </Button>
                </Box>

                {renderUserList()}
            </SettingsSection>

            <UserDetailDialog
                open={dialog === 'user-detail'}
                isCreateUser={isCreatingUser}
                user={selectedUser}
                onClose={onCloseUserDetailDialog}
            />

            {selectedUser && (
                <UserDeleteConfirm
                    open={dialog === 'user-delete-confirm'}
                    user={selectedUser}
                    onClose={() => setDialog('')}
                    onExited={() => setSelectedUserId(-1)}
                />
            )}
        </SettingsLayout>
    )
}
